import React from 'react'
import { format } from 'date-fns'
import { Check, CheckCircle, X, Clock, AlertTriangle, ShoppingBag, Heart } from 'lucide-react'
import toast from 'react-hot-toast'
import { useKidRequests, useUpdateKidRequest, Request } from '@/hooks/useRequests'

const getRequestIcon = (type: string) => {
  const iconMap: Record<string, string> = {
    haircut: '✂️',
    clothes: '👕',
    shoes: '👟',
    school_supplies: '✏️',
    toiletries: '🧴',
    other_need: '📦',
    headache: '🤕',
    stomach: '🤢',
    cold: '🤧',
    injury: '🩹',
    medicine: '💊',
    doctor: '👨‍⚕️',
    internet: '📡',
    computer: '💻',
    phone: '📱',
    car: '🚗',
    room: '🛏️',
    bathroom: '🚿'
  }
  return iconMap[type] || '📝'
}

const getUrgencyColor = (urgency: string) => {
  switch (urgency) {
    case 'urgent': return 'text-red-600 bg-red-50'
    case 'high': return 'text-orange-600 bg-orange-50'
    case 'medium': return 'text-yellow-600 bg-yellow-50'
    default: return 'text-green-600 bg-green-50'
  }
}

export const ParentKidRequestsView: React.FC = () => {
  const { data: requests = [], isLoading } = useKidRequests()
  const updateMutation = useUpdateKidRequest()

  const pendingRequests = requests.filter((r: Request) => r.status === 'pending')
  const recentRequests = requests.filter((r: Request) => r.status !== 'pending').slice(0, 5)

  const handleApprove = async (requestId: string) => {
    try {
      await updateMutation.mutateAsync({ id: requestId, status: 'approved' })
      toast.success('Request approved')
    } catch (error) {
      toast.error('Failed to approve request')
    }
  }

  const handleComplete = async (requestId: string) => {
    try {
      await updateMutation.mutateAsync({ id: requestId, status: 'completed' })
      toast.success('Request marked as done')
    } catch (error) {
      toast.error('Failed to complete request')
    }
  }

  const handleDeny = async (requestId: string) => {
    try {
      await updateMutation.mutateAsync({ id: requestId, status: 'denied' })
      toast.success('Request denied')
    } catch (error) {
      toast.error('Failed to deny request')
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Kid Requests</h2>
        <div className="text-center py-4 text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-4">
        Kid Requests {pendingRequests.length > 0 && (
          <span className="ml-2 bg-red-500 text-white text-sm px-2 py-1 rounded-full">
            {pendingRequests.length}
          </span>
        )}
      </h2>

      {pendingRequests.length === 0 && recentRequests.length === 0 ? (
        <p className="text-gray-500">No kid requests</p>
      ) : (
        <div className="space-y-4">
          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending ({pendingRequests.length})
              </h3>
              <div className="space-y-3">
                {pendingRequests.map((request: Request) => (
                  <div
                    key={request.id}
                    className="border rounded-lg p-4 bg-yellow-50 border-yellow-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{getRequestIcon(request.request_type)}</span>
                        <div>
                          <h4 className="font-semibold">{request.title}</h4>
                          {request.description && (
                            <p className="text-sm text-gray-600 mt-1">{request.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs">
                            <span className="text-gray-500">
                              From: {request.requester_name || 'Unknown'}
                            </span>
                            <span className={`px-2 py-0.5 rounded ${getUrgencyColor(request.urgency || 'medium')}`}>
                              {request.urgency || 'Medium'}
                            </span>
                            <span className="text-gray-400">
                              {format(new Date(request.created_at), 'MMM d, h:mm a')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleComplete(request.id)}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                          title="Mark as done"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleApprove(request.id)}
                          className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeny(request.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          title="Deny"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Completed/Denied Requests */}
          {recentRequests.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-2">Recent</h3>
              <div className="space-y-2">
                {recentRequests.map((request: Request) => (
                  <div
                    key={request.id}
                    className="border rounded-lg p-3 bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{getRequestIcon(request.request_type)}</span>
                        <div>
                          <span className="font-medium">{request.title}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            from {request.requester_name}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Show Mark as Done button for approved requests */}
                        {request.status === 'approved' && (
                          <button
                            onClick={() => handleComplete(request.id)}
                            className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 text-xs"
                            title="Mark as done"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Done
                          </button>
                        )}
                        <span className={`px-2 py-1 rounded text-xs ${
                          request.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          request.status === 'approved' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {request.status === 'completed' ? 'Done' :
                           request.status === 'approved' ? 'Approved' :
                           'Denied'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
